import { useCallback, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface CriminalCorrectionEntry {
  correctionDate: string;
  correctionDetails: string;
}

export interface CriminalCorrectionFormData {
  isAddingEntry?: boolean;
  hasCriminalCorrection: boolean | null;
  criminalCorrections: FormListItem<CriminalCorrectionEntry>[];
  draftCriminalCorrection: CriminalCorrectionEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyCriminalCorrection = (): CriminalCorrectionEntry => ({
  correctionDate: '',
  correctionDetails: '',
});

function CriminalCorrectionPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<CriminalCorrectionFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'criminal-correction-page';

  // Date constraints - within last 7 years up to today
  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 7);

  const handleCriminalCorrectionChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        criminalCorrections: [],
        draftCriminalCorrection: getEmptyCriminalCorrection(),
        hasCriminalCorrection: value,
        isAddingEntry: false,
      });
    } else {
      updateData({
        ...data,
        hasCriminalCorrection: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<CriminalCorrectionEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<CriminalCorrectionEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        criminalCorrections: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: CriminalCorrectionEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.criminalCorrections;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        criminalCorrections: itemsToUse,
        draftCriminalCorrection: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateCriminalCorrection = useCallback((item: CriminalCorrectionEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.correctionDate) {
      errors.correctionDate = 'Date of the conviction, imprisonment, probation, or parole is required';
    }
    if (!item.correctionDetails) {
      errors.correctionDetails = 'Explanation of the conviction, imprisonment, probation, or parole is required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: CriminalCorrectionEntry): boolean => {
    return !draft.correctionDate && !draft.correctionDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: CriminalCorrectionEntry, onChange: (item: CriminalCorrectionEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof CriminalCorrectionEntry>(
        field: K,
        value: CriminalCorrectionEntry[K],
      ) => {
        onChange({
          ...item,
          [field]: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-date`}>
            <DatePicker
              required
              data-testid={`${baseTestId}-entry-date-picker`}
              errorMessage={errors.correctionDate}
              fromDate={minDate}
              id='correctionDate'
              label='Date of the conviction, imprisonment, probation, or parole'
              name='correctionDate'
              toDate={today}
              tooltipText='Enter the date of the conviction, imprisonment, probation, or parole'
              value={item.correctionDate || ''}
              onDateChange={(date, formattedDate) => handleFieldChange('correctionDate', formattedDate || '')}
            />
          </div>

          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.correctionDetails}
              label='Explanation of the conviction, imprisonment, probation, or parole'
              name='correctionDetails'
              rows={5}
              tooltipText='Provide details including an explanation of the violation, place of occurrence, and the name and address of the police department or court involved'
              value={item.correctionDetails || ''}
              onChange={(e) => handleFieldChange('correctionDetails', e.target.value)}
            />
          </div>
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: CriminalCorrectionEntry) => {
    const truncatedDetails =
      item.correctionDetails.length > 50 ? `${item.correctionDetails.substring(0, 50)}...` : item.correctionDetails;

    return {
      details: truncatedDetails,
      title: item.correctionDate,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: CriminalCorrectionEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Date</span>
          <span className='form-list__readonly-value'>{item.correctionDate}</span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.correctionDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    correctionDate: fieldErrors?.['draftCriminalCorrection.correctionDate'],
    correctionDetails: fieldErrors?.['draftCriminalCorrection.correctionDetails'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-criminal-correction`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-criminal-correction-dropdown`}
          errorMessage={fieldErrors?.hasCriminalCorrection}
          label='During the last 7 years, have you been convicted, been imprisoned, been on probation, or been on parole?'
          name='hasCriminalCorrection'
          options={yesNoOptions}
          tooltipText='This question covers any criminal convictions, imprisonments, probations, or parole within the last 7 years. Do not include minor traffic fines of $300 or less.'
          value={data.hasCriminalCorrection}
          onChange={handleCriminalCorrectionChange}
        />
      </div>

      {data.hasCriminalCorrection === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<CriminalCorrectionEntry>
            addLinkText='Add Another Criminal Correction'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftCriminalCorrection}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyCriminalCorrection}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.criminalCorrections}
            label='Conviction, Imprisonment, Probation, or Parole Details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each conviction, imprisonment, probation, or parole within the last 7 years.'
            validateItem={validateCriminalCorrection}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Criminal Corrections'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Conviction, Imprisonment, Probation, or Parole Details</h3>
          <p>
            This section asks about any criminal convictions, imprisonments, probation, or parole you have had in the
            last 7 years. This is required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>
            During the last 7 years, have you been convicted, imprisoned, on probation, or on parole?
          </h4>
          <p>
            You should answer &quot;Yes&quot; and provide details if you have been convicted of a crime, served time in
            prison, been on probation, or been on parole at any point during the last 7 years.
          </p>

          <h4 className='ds-u-margin-top--3'>What to Include</h4>
          <ul>
            <li>Felonies</li>
            <li>Firearms or explosives violations</li>
            <li>Misdemeanors</li>
            <li>All other offenses</li>
            <li>Include convictions resulting from a plea of nolo contendere (no contest)</li>
          </ul>
          <h4 className='ds-u-margin-top--3'>What NOT to Include</h4>
          <ul>
            <li>Minor traffic fines of $300 or less</li>
            <li>Any conviction that has been expunged or sealed by a court</li>
            <li>Convictions set aside under the Federal Youth Corrections Act or similar state law</li>
            <li>Any violation of the law committed before your 16th birthday.</li>
            <li>
              Any violation of law committed before your 18th birthday that was finally decided in juvenile court or
              under a Youth Offender law.
            </li>
            <li>Any conviction for which the record was expunged under Federal or state law.</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Date of the conviction, imprisonment, probation, or parole</h4>
          <p>Enter the start date of the conviction or sentencing, or the date of probation or parole.</p>

          <h4 className='ds-u-margin-top--3'>Explanation of the conviction, imprisonment, probation, or parole</h4>
          <p>
            Enter details about the conviction, imprisonment, probation, or parole. Describe the events that led to it.
          </p>
          {/* TODO: Restore occurrence and police department/court guidance when ACP-787 is completed with those fields. */}

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one event to detail, use the &quot;Add Another Conviction,
            Imprisonment, Probation, or Parole&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default CriminalCorrectionPage;
